import { useEffect, useState } from 'react';
import { Box, Group, Paper, Stack, Tabs, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { MarkAllAsReadButton } from '#/features/notification/ui/MarkAllAsReadButton';
import { InfiniteScroll } from '#/shared/components/InfiniteScroll';
import { useNotificationsLoader } from '#/features/notification/hooks/useNotificationsLoader';
import { useUnreadNotificationsLoader } from '#/features/notification/hooks/useUnreadNotificationsLoader';
import { EmptyPlaceholder } from '#/features/notification/ui/EmptyPlaceholder';
import { NotificationsList, userApi } from '@sinups/ui-kit';
import { AvatarCardWidget } from '#/widgets/avatar-card/AvatarCardWidget';

export default function NotificationsPage() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<string | null>('all');
  const { notifications, isLoading, loadMore, hasMore, lastSeenAt } = useNotificationsLoader({
    initialCount: 20
  });

  const {
    loadMore: loadMoreUnread,
    isLoading: isLoadingUnread,
    notifications: notificationsUnread,
    hasMore: hasMoreUnread,
    lastSeenAt: lastSeenAtUnread,
    refetch: refetchUnreadNotifications,
    unreadCount
  } = useUnreadNotificationsLoader({
    initialCount: 20
  });

  const { data: user, isLoading: isUserLoading } = userApi.useGetCurrentUserQuery();
  const serviceName = user?.organization?.title;

  useEffect(() => {
    if (activeTab === 'unread') {
      refetchUnreadNotifications();
    }
  }, [activeTab, refetchUnreadNotifications]);

  const isUnreadEmpty = notificationsUnread.length === 0;
  const isEmpty = notifications.length === 0;
  const isCurrentTabEmpty = activeTab === 'all' ? isEmpty : isUnreadEmpty;

  return (
    <Paper bg="white" radius="md" px="md" pt="md" h="calc(100vh - 64px - 48px)">
      <Box h="100%" maw="900px">
        <Stack h="100%">
          <Text fw={500} fz="md">
            {t('profile.notifications')}
          </Text>

          <Box style={{ overflow: 'hidden' }}>
            <Tabs value={activeTab} onChange={setActiveTab} keepMounted={false} h="100%">
              <Stack gap={0} h="100%">
                {/* Tabs Header */}
                <Group
                  justify="space-between"
                  wrap="nowrap"
                  gap={0}
                  style={{ borderBottom: '1px solid var(--mantine-color-gray-2)' }}
                >
                  <Tabs.List>
                    <Tabs.Tab value="all">{t('ds:notifications.modal.tabs.all')}</Tabs.Tab>
                    <Tabs.Tab value="unread" disabled={isEmpty}>
                      {t('ds:notifications.modal.tabs.unread')} {unreadCount || ''}
                    </Tabs.Tab>
                  </Tabs.List>
                  <MarkAllAsReadButton />
                </Group>

                <Stack
                  gap={0}
                  style={{
                    justifyContent: isCurrentTabEmpty ? 'center' : 'flex-start',
                    overflow: 'hidden'
                  }}
                >
                  <Tabs.Panel value="all" h="100%">
                    <InfiniteScroll
                      height="100%"
                      onLoadMore={loadMore}
                      isLoading={isLoading || isUserLoading}
                      disabled={!hasMore}
                    >
                      {isEmpty ? (
                        <EmptyPlaceholder
                          title={t('ds:notifications.modal.empty.title')}
                          description={t('ds:notifications.modal.empty.description', {
                            serviceName
                          })}
                        />
                      ) : (
                        <NotificationsList
                          avatarSlot={AvatarCardWidget}
                          notifications={notifications}
                          lastSeenAt={lastSeenAt}
                          py="md"
                        />
                      )}
                    </InfiniteScroll>
                  </Tabs.Panel>

                  <Tabs.Panel value="unread" h="100%">
                    <InfiniteScroll
                      height="100%"
                      onLoadMore={loadMoreUnread}
                      isLoading={isLoadingUnread}
                      disabled={!hasMoreUnread}
                    >
                      {isUnreadEmpty ? (
                        <EmptyPlaceholder
                          title={t('ds:notifications.modal.emptyUnread.title')}
                          description={t('ds:notifications.modal.emptyUnread.description', {
                            serviceName
                          })}
                        />
                      ) : (
                        <NotificationsList
                          avatarSlot={AvatarCardWidget}
                          notifications={notificationsUnread}
                          lastSeenAt={lastSeenAtUnread}
                          py="md"
                        />
                      )}
                    </InfiniteScroll>
                  </Tabs.Panel>
                </Stack>
              </Stack>
            </Tabs>
          </Box>
        </Stack>
      </Box>
    </Paper>
  );
}
